import { HTMLAttributes } from 'react'

import { Heading } from '@hhgtech/hhg-components/components'
import { theme } from '@hhgtech/hhg-components/misc'
import { Skeleton } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Article } from 'interfaces/types'

import {
  StyledArticleContainer,
  StyledArticlesContainer,
  StyledArticlesDesktopContainer,
  StyledArticlesSkeleton,
  StyledContainer,
  StyledFeatureSmallArticleContainer,
  StyledFeatureTopicSeeMore,
  StyledHeaderContainer,
  StyledHeroContainer,
  StyledSmallArticleDesktopContainer,
  StyledSubColumnContainer,
  StyleFeaturedTopics,
} from './index.styled'
import { assetImagePath, basePath } from '../../../interfaces/constants'
import { ArticleCardWrapper } from '../../molecules/articleCardWrapper/index'

export type Props = {
  isLoading?: boolean
  seeMoreLink?: string
  onArticleClick?: (l: string) => void
  expertsArticles?: Article[]
  isMobile?: boolean
  hideUIElements?: boolean
  className?: string
}

const ArticlesSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <StyledArticlesSkeleton {...props}>
      <Skeleton height={50} circle mb="xl" />
      <Skeleton height={8} radius="xl" />
      <Skeleton height={8} mt={6} radius="xl" />
      <Skeleton height={8} mt={6} width="70%" radius="xl" />
    </StyledArticlesSkeleton>
  )
}

const ToolsSuggestionArticles = ({
  isMobile,
  expertsArticles: articles = [],
  isLoading = false,
  hideUIElements,
  seeMoreLink,
  className,
}: // onArticleClick,
Props) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()

  return (
    <>
      {isMobile ? (
        <StyleFeaturedTopics className={`featured-topics ${className}`}>
          {!hideUIElements && (
            <Heading tag="h3" color={theme.colors.gray800}>
              {f({
                id: 'tools.suggestedArticles',
                defaultMessage: 'Suggested articles',
              })}
            </Heading>
          )}
          {isLoading ? (
            <ArticlesSkeleton />
          ) : (
            <StyledContainer>
              {articles && articles.length > 0 && (
                <>
                  <StyledArticleContainer>
                    <Container>
                      <ArticleCardWrapper
                        article={articles[0]}
                        dataEventCategoryForCategory="Tools Category Title"
                        dataEventActionForCategory="Click"
                        dataEventLabelForCategory={`https://${domainLocales[locale]}${articles[0].category.permalinkDecode}`}
                        dataEventCategoryForTitle="Tool Title"
                        dataEventActionForTitle="Click"
                        dataEventLabelForTitle={`https://${domainLocales[locale]}${articles[0]?.permalinkDecode}`}
                        dataEventCategoryForBanner="Tool Image"
                        dataEventActionForBanner="Click"
                        dataEventLabelForBanner={`https://${domainLocales[locale]}${articles[0]?.permalinkDecode}`}
                        size="lg"
                        type="author"
                        direction="vertical"
                        text={''}
                      />
                    </Container>
                  </StyledArticleContainer>
                  <StyledArticlesContainer>
                    {articles.slice(1, 3).map((article: Article) => (
                      <StyledFeatureSmallArticleContainer key={article.id}>
                        <Container>
                          <ArticleCardWrapper
                            article={article}
                            dataEventCategoryForCategory="Tool Category Title"
                            dataEventActionForCategory="Click"
                            dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                            dataEventCategoryForTitle="Tool Title"
                            dataEventActionForTitle="Click"
                            dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                            dataEventCategoryForBanner="Tools Image"
                            dataEventActionForBanner="Click"
                            dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                            type="author"
                            size="md"
                            text=""
                          />
                        </Container>
                      </StyledFeatureSmallArticleContainer>
                    ))}
                  </StyledArticlesContainer>
                </>
              )}
              {!hideUIElements && (
                <StyledFeatureTopicSeeMore>
                  <a
                    href={seeMoreLink || '/'}
                    data-event-category="Categories Dropdown"
                    data-event-action="Click See More"
                    data-event-label={`https://${domainLocales[locale]}/expert`}
                    style={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      color: theme.colors.primaryBase,
                    }}
                  >
                    <Heading
                      tag="h5"
                      color={theme.colors.primaryBase}
                      className="see-more-text"
                    >
                      {f({
                        id: 'home.seeMore',
                        defaultMessage: 'See More',
                      })}
                    </Heading>
                    <img
                      loading="lazy"
                      src={`${assetImagePath}/chevronRightBlue.svg`}
                      style={{ width: '18px', height: '18px' }}
                      alt={f({
                        id: 'home.seeMore',
                        defaultMessage: 'See More',
                      })}
                    />
                  </a>
                </StyledFeatureTopicSeeMore>
              )}
            </StyledContainer>
          )}
        </StyleFeaturedTopics>
      ) : (
        <StyleFeaturedTopics
          className="featured-topics"
          data-is-expert-page={hideUIElements || undefined}
        >
          {/* desktop-heading */}
          {!hideUIElements && (
            <StyledHeaderContainer>
              <Heading tag="h4" color={theme.colors.gray800}>
                {f({
                  id: 'tools.suggestedArticles',
                  defaultMessage: 'Suggested Articles',
                })}
              </Heading>
              <StyledFeatureTopicSeeMore>
                <a
                  href={seeMoreLink || '/'}
                  data-event-category="Categories Dropdown"
                  data-event-action="Click See More"
                  data-event-label={`https://${domainLocales[locale]}${basePath}/expert`}
                  style={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    color: theme.colors.primaryBase,
                  }}
                >
                  <Heading
                    tag="h5"
                    color={theme.colors.primaryBase}
                    className="see-more-text"
                  >
                    {f({
                      id: 'home.seeMore',
                      defaultMessage: 'See More',
                    })}
                  </Heading>
                  <img
                    loading="lazy"
                    src={`${assetImagePath}/chevronRightBlue.svg`}
                    style={{ width: '18px', height: '18px' }}
                    alt={f({
                      id: 'home.seeMore',
                      defaultMessage: 'See More',
                    })}
                  />
                </a>
              </StyledFeatureTopicSeeMore>
            </StyledHeaderContainer>
          )}

          {/* desktop-articles */}
          <StyledArticlesDesktopContainer>
            {isLoading ? (
              <ArticlesSkeleton />
            ) : (
              articles &&
              articles.length > 0 && (
                <>
                  <StyledHeroContainer key={articles[0].id}>
                    <ArticleCardWrapper
                      dataEventCategoryForCategory="Tool Category Title"
                      dataEventActionForCategory="Click"
                      dataEventLabelForCategory={`https://${domainLocales[locale]}${articles[0]?.category.permalinkDecode}`}
                      dataEventCategoryForTitle="Tool Title"
                      dataEventActionForTitle="Click"
                      dataEventLabelForTitle={`https://${domainLocales[locale]}${articles[0]?.permalinkDecode}`}
                      dataEventCategoryForBanner="Tool Image"
                      dataEventActionForBanner="Click"
                      dataEventLabelForBanner={`https://${domainLocales[locale]}${articles[0]?.permalinkDecode}`}
                      type="author"
                      size="lg"
                      direction="vertical"
                      article={articles[0]}
                      text={''}
                      styleLabel={{
                        fontSize: theme.sizes.fsHeading6,
                      }}
                    />
                  </StyledHeroContainer>
                  <StyledSubColumnContainer>
                    {articles.slice(1, 3).map((article: Article) => (
                      <StyledSmallArticleDesktopContainer key={article.id}>
                        <ArticleCardWrapper
                          dataEventCategoryForCategory="Tool Category Title"
                          dataEventActionForCategory="Click"
                          dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                          dataEventCategoryForTitle="Tool Title"
                          dataEventActionForTitle="Click"
                          dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                          dataEventCategoryForBanner="Tool Image"
                          dataEventActionForBanner="Click"
                          dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                          type="author"
                          size="sm"
                          direction="vertical"
                          article={article}
                          text={''}
                          className="desktop-article"
                          styleLabel={{
                            fontSize: theme.sizes.fsCaption2,
                          }}
                        />
                      </StyledSmallArticleDesktopContainer>
                    ))}
                  </StyledSubColumnContainer>
                </>
              )
            )}
          </StyledArticlesDesktopContainer>
        </StyleFeaturedTopics>
      )}
    </>
  )
}

export { ToolsSuggestionArticles }
